<template>
  <AdminPageView :loading :loadingErrorMessage :title="`${GetCivilityShortLabel(user?.civility)} ${user?.lastName} ${user?.firstName}`">
    <template #actions>
      <button class="btn btn-outline-success text-nowrap" @click="saveUser" :class="{ 'disabled': isSubmitting }"><i class="fa-solid  pe-2" :class="[props.id ? 'fa-floppy-disk' : 'fa-plus']"></i> {{ props.id ? 'Enregistrer' : 'Ajouter' }}</button>
    </template>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-12 col-md-2">
            <SelectInputComponent
              value-key="civility"
              label="Civilité"
              placeholder="Sélectionnez une civilité"
              :required="true"
              class="mb-3 "
              :options="Object.values(CivilityEnum).map(type => ({ value: type, label: GetCivilityShortLabel(type) }))"
            />
          </div>

          <div class="col-12 col-md-5">
            <TextInputComponent
              value-key="lastName"
              label="Nom"
              placeholder="Nom"
              :required="true"
              class="mb-3"
              :upper-case="true"
            />
          </div>

          <div class="col-12 col-md-5">
            <TextInputComponent
              value-key="firstName"
              label="Prénom"
              placeholder="Prénom"
              :required="true"
              class="mb-3"
              :capitalize="true"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-7">
            <TextInputComponent
              value-key="email"
              label="Email"
              placeholder="Email"
              :required="false"
              class="mb-3"
            />
            <!-- Alerte doublon email (création uniquement) -->
            <div v-if="!props.id && emailCheck" class="mb-3">
              <div v-if="emailCheck.exists && !emailCheck.isDeleted" class="alert alert-warning mb-0">
                <i class="fa-solid fa-triangle-exclamation text-warning me-2"></i>
                Un utilisateur actif avec cet email existe déjà :
                <strong>{{ emailCheck.user?.firstName }} {{ emailCheck.user?.lastName?.toUpperCase() }}</strong>.
                <router-link
                  :to="{ name: 'admin-edit-participant', params: { id: emailCheck.user?.id } }"
                  class="alert-link ms-1"
                >
                  Modifier cet utilisateur
                </router-link>
              </div>
              <div v-else-if="emailCheck.exists && emailCheck.isDeleted" class="alert alert-info mb-0">
                <i class="fa-solid fa-user-slash me-2"></i>
                Un utilisateur supprimé avec cet email existe :
                <strong>{{ emailCheck.user?.firstName }} {{ emailCheck.user?.lastName?.toUpperCase() }}</strong>.
                <button
                  class="btn btn-sm btn-outline-primary ms-2"
                  @click="reactivateUser"
                  :disabled="isReactivating"
                >
                  <i class="fa-solid fa-rotate-left me-1"></i>Réactiver
                </button>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-5">
            <TextInputComponent
              value-key="company"
              label="Entreprise"
              placeholder="Entreprise"
              :required="false"
              class="mb-3"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <PhoneInputComponent
              value-key="mobilePhone"
              label="Téléphone mobile"
              placeholder="Téléphone mobile"
              :required="false"
              class="mb-3"
              v-model:is-valid="isMobilePhoneValid"
            />
          </div>
          <div class="col-12 col-md-6">
            <DatePickerComponent
              value-key="birthDate"
              :max-date="new Date()"
              :placeholder="'Date de naissance'"
              :label="'Date de naissance'"
              :required="false"
              class="mb-3"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <TextInputComponent
              value-key="passportNumber"
              placeholder="Numéro de passeport"
              label="Numéro de passeport"
              :required="false"
              class="mb-3"
              :upper-case="true"
            />
          </div>
          <div class="col-12 col-md-6">
          <DatePickerComponent
            value-key="passportExpirationDate"
            :min-date="new Date()"
            placeholder="Date d'expiration du passeport"
            label="Date d'expiration du passeport"
            :required="false"
            class="mb-3"
          />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <TextInputComponent
              value-key="estaNumber"
              placeholder="Numéro ESTA"
              label="Numéro ESTA"
              :required="false"
              name="esta-authorization"
              autocomplete="one-time-code"
              class="mb-3"
            />
          </div>
        </div>

        <hr>

        <div v-if="props.id" class="row">
          <div class="col-12 col-md-6">
            <TextInputComponent
              value-key="password"
              type="password"
              label="Nouveau mot de passe (laisser vide pour ne pas modifier)"
              placeholder="Nouveau mot de passe (laisser vide pour ne pas modifier)"
              :required="false"
              autocomplete="new-password"
              class="mb-3"
            />
          </div>
          <div class="col-12 d-flex align-items-center mb-3">
            <CheckboxComponent
              value-key="isAdmin"
              label="Administrateur"
              :required="true"
              class="mb-3"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Participations (mode édition uniquement) -->
    <div v-if="props.id" class="card mt-4">
      <div class="card-header">
        Evénements
        <span class="badge bg-primary ms-2 align-middle">{{ user.participationsBasic?.length ?? 0 }}</span>
      </div>
      <div class="card-body p-0">
        <template v-if="user.participationsBasic && user.participationsBasic.length > 0">
          <ul class="list-group list-group-flush">
            <li
              v-for="tp in user.participationsBasic"
              :key="tp.travelId"
              class="list-group-item"
            >
              <router-link
                :to="{ name: 'admin-edit-travel-participants', params: { id: tp.travelId } }"
                class="btn btn-link link-underline link-underline-opacity-0 p-0"
              >
                {{ tp.travelName }} {{ formatDatePeriod(tp.travelStartDate, tp.travelEndDate) }}
              </router-link>
            </li>
          </ul>
        </template>
        <p v-else class="text-muted mb-0 p-3">
          <i class="fa-solid fa-circle-info me-2"></i>Aucune participation.
        </p>
      </div>
    </div>
  </AdminPageView>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { useForm } from 'vee-validate';
import { useNotificationsStore } from '@/stores/notificationStore';

import { toTypedSchema } from '@vee-validate/zod';
import * as zod from 'zod';
import { IdNotDefined, useConfirmDialog, GetCivilityShortLabel, formatDatePeriod } from '@/compositions/helper';

import AdminApi from '@/api/adminApi'
import AdminPageView from '@/views/admin/AdminPageView.vue';

const { confirmDialog } = useConfirmDialog();
const notificationsStore = useNotificationsStore();
const router = useRouter()

const loading = ref(true);
const loadingErrorMessage = ref<string|undefined>(undefined);
const isMobilePhoneValid = ref<boolean>(true);
const emailCheck = ref<CheckEmailResponse | null>(null);
const isReactivating = ref(false);
let emailCheckTimer: ReturnType<typeof setTimeout> | null = null;

const props = defineProps<{
  id?: number
  travelId?: number
}>();

const defaultUser = <AdminUser>{
  id: IdNotDefined,
  firstName: '',
  lastName: '',
  email: '',
  mobilePhone: ''
}

const {
  handleSubmit,
  isSubmitting,
  meta,
  resetForm,
  values: user
} = useForm<AdminUser>({
  initialValues: defaultUser,
  validationSchema: toTypedSchema(
    zod.object({
      id: zod.number().nonnegative(),
      isAdmin: zod.boolean().optional(),
      lastUpdateDate: zod.date().optional(),
      civility: zod.nativeEnum(CivilityEnum, { message: 'Civilité non valide' }),
      firstName: zod.string({message: "Le prénom est requis"}).min(2, "Le prénom est trop court").max(100,"Le prénom est trop long"),
      lastName: zod.string({message: "Le nom est requis"}).min(2, "Le nom est trop court").max(100,"Le nom est trop long"),
      email: zod.string().email("L'email est invalide").optional().or(zod.literal('')),
      company: zod.string().max(100,"Le nom de l'entreprise est trop long").optional(),
      mobilePhone: zod.string().optional().refine(() => isMobilePhoneValid.value, {message: "Le numéro de téléphone mobile est invalide"}),
      birthDate: zod.date().min(new Date(1900, 0, 1), "Date de naissance invalide").optional(),
      passportNumber: zod.string().min(5, "Numéro de passeport trop court").max(50, "Numéro de passeport trop long").nullish().or(zod.literal('')),
      passportExpirationDate: zod.date().min(new Date(), "Date d'expiration du passeport invalide").nullish(),
      estaNumber: zod.string().min(2, 'Numéro ESTA trop court').max(50, 'Numéro ESTA trop long').nullish(),
      password: zod.string().min(4, 'Le mot de passe doit contenir au moins 4 caractères').max(100, 'Mot de passe trop long').optional().or(zod.literal('')),
      participationsBasic: zod.array(zod.object({
        travelId: zod.number(),
        travelName: zod.string(),
        travelStartDate: zod.date(),
        travelEndDate: zod.date()
      })).nullish()
    })
  )
});

const load = () => {
  if (props.id) {
    AdminApi.getUser(props.id).then((response) => {
      resetForm({values: response.data});
    }).catch((error: Error) => {
      loadingErrorMessage.value = 'Impossible de charger le participant';
      console.error('Error loading participant:', error);
    }).finally(() => {
      loading.value = false;
    });
  } else {
    resetForm({ values: defaultUser });
    loading.value = false;
  }
};

const saveUser = async () => {
  handleSubmit(async (values) => {
    await AdminApi.addUpdateUser(values).then(async (newUser) => {
      resetForm({values: newUser.data});
      meta.value.dirty = false;
      if(props.travelId && props.travelId > 0 && newUser.data?.id && newUser.data.id > 0) {
        await AdminApi.addParticipant(newUser.data.id,props.travelId).then(() => {
          notificationsStore.notifySuccess('Participant ajouté à l\'événement avec succès.');
          router.back();
        }).catch((error: Error) => {
          notificationsStore.notifyError('Erreur lors de l\'ajout du participant au voyage',error.message);
        });
      }
      else {
        notificationsStore.notifySuccess('Participant mis à jour avec succès.');
        router.back();
      }
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de la mise à jour du participant',error.message);
    });
  },(result) => {
      const errors = Object.keys(result.errors)
      .map((key:string) => {
        return result.errors[key as keyof typeof result.errors]
      });

      notificationsStore.notifyError(errors.slice(0, 5).join('<br>'));
  })()
}

const reactivateUser = async () => {
  if (!emailCheck.value?.user?.id) return;
  isReactivating.value = true;
  AdminApi.reactivateUser(emailCheck.value.user.id).then((response) => {
    notificationsStore.notifySuccess('Utilisateur réactivé avec succès.');
    router.push({ name: 'admin-edit-participant', params: { id: response.data.id } });
  }).catch((error: Error) => {
    notificationsStore.notifyError('Erreur lors de la réactivation de l\'utilisateur', error.message);
  }).finally(() => {
    isReactivating.value = false;
  });
};

onMounted(() => {
  load();
});

onBeforeRouteLeave(() => {
  if(meta.value.dirty) {
    return confirmDialog("Vous avez des modifications non enregistrées.<br>Voulez-vous vraiment quitter cette page ?");
  } else {
  return true
  }
});

watch(() => props.id, () => {
    load()
});

// Vérification doublon email (création uniquement)
watch(() => user.email, (email) => {
  if (emailCheckTimer) clearTimeout(emailCheckTimer);
  emailCheck.value = null;

  if (props.id || !email || email.length < 5 || !email.includes('@')) return;

  emailCheckTimer = setTimeout(() => {
    AdminApi.checkEmail(email).then((response) => {
      if (response.data.exists) {
        emailCheck.value = response.data;
      }
    }).catch(() => {
      // Silently ignore check errors
    });
  }, 500);
});
</script>
